import { Handle, Node, NodeProps } from "@xyflow/react";
import { type ReactNode } from "react";
import cn from "classnames";
import { twMerge } from "tailwind-merge";
import Conditional from "~/components/Playground/Node/BaseNode/Conditional";
import { State, Outcome } from "~/lib/pipeline/Step";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import { bgColorForStep, textColorForStep } from "~/components/Playground/Node/colors";
import { useBuild, useMostRecentJob } from "~/components/build/Show/lib/BuildContext";
import { useTrack } from "~/components/build/Show/lib/useTrack";

type BaseStepProps = Node<{
  state?: State | null;
  outcome?: Outcome | null;
  if?: string;
  emphasis?: Emphasis;
  type?: string;
}>;

export enum Emphasis {
  Default,
  High,
  Low,
}

export default function BaseNode(
  props: {
    label?: ReactNode;
    icon?: ReactNode;
    children?: ReactNode;
    className?: string;
    hideStateIcon?: boolean;
    count?: ReactNode;
    actions?: ReactNode;
    disabled?: boolean;
  } & NodeProps<BaseStepProps>,
) {
  const { state, outcome, emphasis = Emphasis.Default, type } = props.data;
  const { build } = useBuild({ strict: false });
  const track = useTrack();

  const job = useMostRecentJob(props.id);
  const waitingOnConcurrencyGroup = job?.state === "limited";

  return (
    <>
      <Conditional if={props.data.if} />
      <div
        onClick={() => track("Build Canvas Step Clicked", { type })}
        data-state={state}
        data-outcome={outcome}
        aria-disabled={props.disabled}
        className={twMerge(
          "aria-disabled:cursor-not-allowed",
          "relative",
          "bg-white",
          "cursor-pointer",
          "rounded-xl",
          "p-3",
          "border-2",
          ["flex", "flex-col", "gap-3"],
          "transition-[color,opacity] duration-150",
          "max-w-[45ch]",
          cn({
            [textColorForStep({ ...props.data, waitingOnConcurrencyGroup })]: state,
            [bgColorForStep(props.data, build)]: state && build,
            "shadow-depth-200": !state,
            "opacity-40 hover:opacity-100":
              state === State.WaitingForDependencies || state === State.Ignored,
            border: state === State.Finished && outcome === Outcome.SoftFailed,
            "ring-4 ring-[#086DD7] ring-offset-1": props.selected,
          }),
          props.className,
          cn({
            "opacity-40 hover:opacity-100": emphasis === Emphasis.Low,
            "border-opacity-100 opacity-100": emphasis === Emphasis.High,
          }),
        )}
      >
        <div className="flex items-center gap-2">
          {props.label && (
            <header
              role="heading"
              className={twMerge(
                "min-w-0 max-w-full flex-auto truncate text-base font-medium text-slate-900",
                cn({
                  "line-through": state === State.Ignored,
                }),
              )}
            >
              {props.label}
            </header>
          )}

          <span className="ml-auto flex items-center gap-1">
            {props.count}
            {props.icon ? (
              props.icon
            ) : (
              <StateIcon {...props.data} waitingOnConcurrencyGroup={waitingOnConcurrencyGroup} />
            )}
          </span>

          {props.actions && props.actions}
        </div>
        {props.children}
      </div>

      {props.targetPosition && (
        <Handle
          type="target"
          position={props.targetPosition}
          isConnectable={props.isConnectable}
          className={cn({ "opacity-0": !props.isConnectable })}
        />
      )}
      {props.sourcePosition && (
        <Handle
          type="source"
          position={props.sourcePosition}
          isConnectable={props.isConnectable}
          className={cn({ "opacity-0": !props.isConnectable })}
        />
      )}
    </>
  );
}
